/**
 * Receiving — Supplier Invoice list view · v1.2 Document Intelligence.
 *
 * Operators land here after fuel arrives from Suncor. Drop the invoice
 * PDF → the parser extracts every field → review + approve → FIFO
 * layer + tank inventory increment happen automatically.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UploadSimple, FileText, CheckCircle, Clock, ArrowRight, Sparkle, Warning } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { fmtCalendarDateShort } from "@/lib/calendarDate";
import { ReceivingSubNav } from "@/pages/FreightExceptions";
import EmptyState from "@/components/common/EmptyState";

export default function Receiving() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [lowConfidenceOnly, setLowConfidenceOnly] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/receiving", {
        params: lowConfidenceOnly ? { low_confidence: true } : undefined,
      });
      setRows(data.invoices || []);
    } catch (e) {
      toast.error("Could not load supplier invoices.");
    } finally {
      setLoading(false);
    }
  }, [lowConfidenceOnly]);
  useEffect(() => { load(); }, [load]);
  // Sprint 10 · WORKSTREAM 1 · 2026-08-13. Refresh the list after a
  // bulk-include so the "Included" badges appear immediately.
  useEffect(() => {
    const h = () => load();
    window.addEventListener("receiving:refresh", h);
    return () => window.removeEventListener("receiving:refresh", h);
  }, [load]);

  const doUpload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please upload a PDF file.");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/receiving/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Invoice parsed — review before approving.");
      nav(`/receiving/${data.id}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const kind = typeof detail === "object" ? detail?.kind : null;
      if (kind === "duplicate_invoice") {
        toast.info(detail.message);
        if (detail.existing_id) nav(`/receiving/${detail.existing_id}`);
      } else {
        const msg = typeof detail === "string" ? detail : detail?.message || "Upload failed";
        toast.error(msg);
      }
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) doUpload(file);
  };

  return (
    <div className="p-6 space-y-6" data-testid="receiving-page">
      <ReceivingSubNav active="receipts" />
      <header>
        <div className="text-xs text-slate-500 uppercase tracking-wide">v1.2 · Document Intelligence</div>
        <h1 className="font-display text-2xl font-semibold text-slate-900">Receiving</h1>
        <p className="text-sm text-slate-600 max-w-3xl mt-1">
          Drop a supplier invoice PDF. The parser extracts every field, matches it against the approved Price Notification and delivered cost, and lets you approve with one click. On approval — FIFO cost layer created, tank inventory incremented, invoice archived.
        </p>
      </header>

      {/* Drop zone */}
      <div
        data-testid="receiving-dropzone"
        onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        className={[
          "flex cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-14 text-center transition-colors",
          dragActive
            ? "border-emerald-500 bg-emerald-50"
            : "border-slate-300 bg-slate-50 hover:border-emerald-400 hover:bg-emerald-50/50",
        ].join(" ")}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf,.pdf"
          className="hidden"
          data-testid="receiving-file-input"
          onChange={(e) => doUpload(e.target.files?.[0])}
        />
        <UploadSimple size={44} weight="regular" className={dragActive ? "text-emerald-600" : "text-slate-500"} />
        <div className="text-lg font-semibold text-slate-900">
          {uploading ? "Extracting invoice…" : "Receive Supplier Invoice"}
        </div>
        <div className="text-sm text-slate-600">
          {uploading ? "Reading fields from the PDF, this only takes a second." : "Drop PDF here or click to browse"}
        </div>
      </div>

      {/* Sprint 9.2 · Low-confidence filter */}
      <FilterStrip
        lowConfidenceOnly={lowConfidenceOnly}
        onToggle={() => setLowConfidenceOnly((v) => !v)}
      />

      {/* History — grouped by state */}
      <ReceivingSections rows={rows} loading={loading} nav={nav} />
    </div>
  );
}

function FilterStrip({ lowConfidenceOnly, onToggle }) {
  return (
    <div className="flex items-center justify-between" data-testid="recv-filter-strip">
      <div className="flex items-center gap-2">
        <Sparkle size={14} className="text-slate-500" />
        <span className="text-xs text-slate-600">
          The system trusts high-confidence invoices automatically. Toggle to focus on what needs your judgement.
        </span>
      </div>
      <button
        type="button"
        onClick={onToggle}
        data-testid="recv-low-confidence-toggle"
        aria-pressed={lowConfidenceOnly}
        className={[
          "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors",
          lowConfidenceOnly
            ? "border-amber-300 bg-amber-100 text-amber-900"
            : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50",
        ].join(" ")}
      >
        <Warning size={12} weight="fill" />
        {lowConfidenceOnly ? "Showing low-confidence only" : "Review low-confidence only"}
      </button>
    </div>
  );
}

function ReceivingSections({ rows, loading, nav }) {
  // Sprint 10 · WORKSTREAM 1 · 2026-08-13. Multi-Select Invoices for
  // Daily Package inclusion. Business-date state is authoritative on
  // the request — an operator reviewing Aug 12 can only include Aug 12
  // invoices, even if the browser shows other-day rows.
  const today = new Date().toISOString().slice(0, 10);
  const [businessDate, setBusinessDate] = useState(today);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (loading) return <div className="text-sm text-slate-500" data-testid="recv-loading">Loading…</div>;
  if (!rows.length) {
    return (
      <EmptyState
        icon={FileText}
        title="No supplier invoices yet"
        why="This list shows every fuel PDF you've uploaded from Suncor, sorted by upload date."
        next="Drag your Suncor invoice into the upload area above, or paste one in with the file picker."
        testid="recv-empty"
      />
    );
  }
  const pending  = rows.filter((r) => r.status !== "approved");
  const approvedToday = rows.filter((r) => r.status === "approved" && (r.approved_at || "").startsWith(today));
  const history       = rows.filter((r) => r.status === "approved" && !(r.approved_at || "").startsWith(today));

  // Selection lives at the parent so Select All operates across the
  // currently-eligible approved rows (today + history), while pending /
  // exception invoices remain non-selectable.
  const selectableRows = [...approvedToday, ...history].filter(
    (r) => !(r.included_in_daily_package_for && r.included_in_daily_package_for !== businessDate),
  );
  const selectableIds = new Set(selectableRows.map((r) => r.id));
  // An invoice is selectable if approved AND not already included for
  // the current business_date. Already-included rows for the *same*
  // date show as "Included" and cannot be re-selected.
  const isEligible = (r) =>
    r.status === "approved" && r.included_in_daily_package_for !== businessDate;
  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const eligibleSelectableIds = selectableRows.filter(isEligible).map((r) => r.id);
  const allEligibleSelected = eligibleSelectableIds.length > 0
    && eligibleSelectableIds.every((id) => selectedIds.has(id));
  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (allEligibleSelected) return new Set();
      return new Set([...prev, ...eligibleSelectableIds]);
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  // Selection summary — count + litres + dollar total from the row
  // data the backend already surfaces.
  const selectedRows = selectableRows.filter((r) => selectedIds.has(r.id));
  const summary = selectedRows.reduce((acc, r) => {
    const l = parseFloat(r.total_litres || 0);
    const d = parseFloat(r.amount_due || 0);
    return {
      count:  acc.count + 1,
      litres: acc.litres + (Number.isFinite(l) ? l : 0),
      dollars: acc.dollars + (Number.isFinite(d) ? d : 0),
    };
  }, { count: 0, litres: 0, dollars: 0 });

  const submitBulkInclude = async () => {
    setSubmitting(true);
    try {
      const ids = Array.from(selectedIds);
      const { data } = await api.post("/receiving/bulk-include-in-package", {
        business_date: businessDate,
        invoice_ids:   ids,
      });
      const incl = data.included_count || 0;
      const skip = data.skipped_count || 0;
      const rej  = data.rejected_count || 0;
      if (incl > 0) {
        toast.success(
          `Included ${incl} invoice${incl === 1 ? "" : "s"} in the Daily Package for ${businessDate}.`
          + (skip ? ` ${skip} already included and skipped.` : "")
          + (rej ? ` ${rej} rejected.` : ""),
        );
      } else if (skip > 0 && rej === 0) {
        toast.info(`All ${skip} invoice${skip === 1 ? "" : "s"} already included — no change.`);
      } else {
        toast.error(`No invoices included · ${rej} rejected.`);
      }
      setSelectedIds(new Set());
      setConfirming(false);
      // Refresh the underlying list so the "Included" badges appear.
      window.dispatchEvent(new Event("receiving:refresh"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to include invoices.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="recv-sections">
      <div className="grid grid-cols-3 gap-3">
        <StatCard label="Pending Review"  count={pending.length}       tone="amber" testid="recv-stat-pending" />
        <StatCard label="Approved Today"  count={approvedToday.length} tone="emerald" testid="recv-stat-today" />
        <StatCard label="History"         count={history.length}       tone="slate" testid="recv-stat-history" />
      </div>

      {/* Multi-Select · Include-in-Package toolbar. Sticky selection
          summary appears once the operator picks the first row. */}
      <MultiSelectToolbar
        businessDate={businessDate}
        onBusinessDateChange={(d) => { setBusinessDate(d); setSelectedIds(new Set()); }}
        summary={summary}
        allSelected={allEligibleSelected}
        eligibleCount={eligibleSelectableIds.length}
        onSelectAll={toggleSelectAll}
        onClear={clearSelection}
        onSubmit={() => setConfirming(true)}
        submitting={submitting}
      />

      <Section title="Pending Review"  rows={pending}       nav={nav} testid="recv-section-pending" emptyLabel="Nothing waiting for review." />
      <Section
        title="Approved Today"
        rows={approvedToday}
        nav={nav}
        testid="recv-section-today"
        emptyLabel="No invoices approved today yet."
        selectable
        selectedIds={selectedIds}
        onToggle={toggle}
        businessDate={businessDate}
      />
      <Section
        title="History"
        rows={history}
        nav={nav}
        testid="recv-section-history"
        emptyLabel="No prior invoices."
        selectable
        selectedIds={selectedIds}
        onToggle={toggle}
        businessDate={businessDate}
      />

      {confirming ? (
        <ConfirmDialog
          businessDate={businessDate}
          summary={summary}
          onCancel={() => setConfirming(false)}
          onConfirm={submitBulkInclude}
          submitting={submitting}
        />
      ) : null}
    </div>
  );
}

function MultiSelectToolbar({
  businessDate, onBusinessDateChange, summary,
  allSelected, eligibleCount, onSelectAll, onClear,
  onSubmit, submitting,
}) {
  const fmt = (v) => Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 });
  const fmtM = (v) => `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <section
      data-testid="recv-multiselect-toolbar"
      className="rounded-md border border-slate-200 bg-white px-4 py-3"
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 justify-between">
        <div className="flex items-center gap-2">
          <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
            Include for business date
          </label>
          <input
            type="date"
            value={businessDate}
            onChange={(e) => onBusinessDateChange(e.target.value)}
            className="rounded border border-slate-300 bg-white px-2 py-1 text-xs font-mono focus:border-slate-500 focus:outline-none"
            data-testid="recv-multiselect-date"
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onSelectAll}
            disabled={eligibleCount === 0}
            className="rounded border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="recv-multiselect-select-all"
          >
            {allSelected ? "Deselect All" : "Select All Eligible"}
          </button>
          <button
            type="button"
            onClick={onClear}
            disabled={summary.count === 0}
            className="rounded border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="recv-multiselect-clear"
          >
            Clear
          </button>
          <div
            className="rounded border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-mono text-slate-700"
            data-testid="recv-multiselect-summary"
          >
            <b>{summary.count}</b> selected · <b>{fmt(summary.litres)} L</b>
            {summary.dollars > 0 ? <> · <b>{fmtM(summary.dollars)}</b></> : null}
          </div>
          <button
            type="button"
            onClick={onSubmit}
            disabled={summary.count === 0 || submitting}
            className="rounded bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="recv-multiselect-submit"
          >
            Include Selected in Daily Package
          </button>
        </div>
      </div>
      <p className="mt-2 text-[10px] italic text-slate-500">
        Only approved invoices can be included. Rows already included for the selected business date
        appear as <b>Included</b> and cannot be re-selected. Server-side duplicate protection is enforced.
      </p>
    </section>
  );
}

function ConfirmDialog({ businessDate, summary, onCancel, onConfirm, submitting }) {
  const fmt = (v) => Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 });
  const fmtM = (v) => `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4"
      data-testid="recv-multiselect-confirm-dialog"
    >
      <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white shadow-xl">
        <header className="border-b border-slate-200 px-5 py-3">
          <div className="text-sm font-semibold text-slate-900">Include invoices in Daily Package</div>
        </header>
        <div className="px-5 py-4 text-sm text-slate-700 space-y-2">
          <p>
            You are about to include <b>{summary.count} invoice{summary.count === 1 ? "" : "s"}</b>{" "}
            (<b>{fmt(summary.litres)} L</b>{summary.dollars > 0 ? <>, <b>{fmtM(summary.dollars)}</b></> : null})
            in the Daily Package for <b className="font-mono">{businessDate}</b>.
          </p>
          <p className="text-xs text-slate-500 italic">
            This action is idempotent — already-included invoices are skipped.
            Rows attached to a different business date are rejected.
          </p>
        </div>
        <footer className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
            data-testid="recv-multiselect-confirm-cancel"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-40"
            data-testid="recv-multiselect-confirm-submit"
            disabled={submitting}
          >
            {submitting ? "Including…" : "Include"}
          </button>
        </footer>
      </div>
    </div>
  );
}

function StatCard({ label, count, tone, testid }) {
  const toneCls = tone === "amber"   ? "border-amber-200 bg-amber-50 text-amber-900"
                : tone === "emerald" ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                                     : "border-slate-200 bg-white text-slate-800";
  return (
    <div className={`rounded-md border ${toneCls} px-4 py-3`} data-testid={testid}>
      <div className="text-[11px] font-semibold uppercase tracking-wider opacity-80">{label}</div>
      <div className="text-2xl font-semibold tabular-nums mt-0.5">{count}</div>
    </div>
  );
}

function Section({ title, rows, nav, testid, emptyLabel, selectable, selectedIds, onToggle, businessDate }) {
  return (
    <section data-testid={testid}>
      <h2 className="mb-2 font-display text-sm font-semibold uppercase tracking-wider text-slate-600">{title}</h2>
      {rows.length === 0 ? (
        <div className="rounded-md border border-slate-200 bg-white px-4 py-3 text-xs text-slate-500">{emptyLabel}</div>
      ) : (
        <ul className="divide-y divide-slate-200 rounded-md border border-slate-200 bg-white">
          {rows.map((r) => {
            // Sprint 10 · WORKSTREAM 1 · 2026-08-13.
            // Include-in-package eligibility:
            //   * approved
            //   * not already included for the selected business date
            //   * not already attached to a *different* business date
            //     (must clear first — server rejects too)
            const alreadyIncludedHere = selectable && r.included_in_daily_package_for === businessDate;
            const attachedElsewhere   = selectable && r.included_in_daily_package_for
                                        && r.included_in_daily_package_for !== businessDate;
            const eligible = selectable && r.status === "approved"
                             && !alreadyIncludedHere && !attachedElsewhere;
            const checked  = selectable && selectedIds?.has(r.id);
            return (
              <li key={r.id}>
                <div
                  data-testid={`recv-row-${r.id}`}
                  className="flex w-full items-center gap-3 px-4 py-3"
                >
                  {selectable ? (
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-slate-900 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                      checked={!!checked}
                      disabled={!eligible}
                      onChange={() => onToggle?.(r.id)}
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`recv-select-${r.id}`}
                      aria-label={eligible ? `Select invoice ${r.invoice_number || r.id}` : `Invoice ${r.invoice_number || r.id} not selectable`}
                      title={
                        alreadyIncludedHere ? `Already included in the Daily Package for ${businessDate}`
                        : attachedElsewhere ? `Already included for ${r.included_in_daily_package_for} — clear first`
                        : r.status !== "approved" ? "Approve this invoice before including"
                        : "Include in the Daily Package"
                      }
                    />
                  ) : null}
                  <button
                    type="button"
                    onClick={() => nav(`/receiving/${r.id}`)}
                    className="flex flex-1 items-center justify-between gap-4 text-left hover:bg-slate-50 -mx-2 px-2 py-1 rounded"
                  >
                    <div className="flex items-center gap-3">
                      <FileText size={20} className="text-slate-500" />
                      <div>
                        <div className="text-sm font-semibold text-slate-900">
                          {r.supplier} · Invoice #{r.invoice_number || "—"}
                        </div>
                        <div className="text-[11px] text-slate-500">
                          Delivery {fmtCalendarDateShort(r.delivery_date)} · {r.terminal || "—"} · {r.amount_due ? `$${r.amount_due}` : "—"}
                          {r.total_litres ? (
                            <> · {Number(r.total_litres).toLocaleString(undefined, { maximumFractionDigits: 0 })} L</>
                          ) : null}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {alreadyIncludedHere ? (
                        <span
                          data-testid={`recv-included-badge-${r.id}`}
                          className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-800"
                          title="Included in the Daily Package for this business date"
                        >
                          Included
                        </span>
                      ) : attachedElsewhere ? (
                        <span
                          data-testid={`recv-included-elsewhere-${r.id}`}
                          className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-700"
                          title={`Included in the Daily Package for ${r.included_in_daily_package_for}`}
                        >
                          Included · {r.included_in_daily_package_for}
                        </span>
                      ) : null}
                      {r.business_state_label && (
                        <span
                          data-testid={`recv-lifecycle-${r.business_state}`}
                          className={[
                            "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold",
                            r.business_state === "landed_cost_complete" || r.business_state === "archived"
                              ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                              : r.business_state === "cancelled"
                                ? "border-rose-300 bg-rose-50 text-rose-800"
                                : r.business_state === "exception"
                                  ? "border-amber-300 bg-amber-50 text-amber-800"
                                  : "border-sky-300 bg-sky-50 text-sky-800",
                          ].join(" ")}
                          title="ARC-015 · Receipt Reconciliation lifecycle"
                        >
                          {r.business_state_label}
                        </span>
                      )}
                      <ConfidencePill c={r.confidence} />
                      <StatusBadge status={r.status} />
                      <ArrowRight size={14} className="text-slate-400" />
                    </div>
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function ConfidencePill({ c }) {
  if (!c || c.overall == null) {
    return (
      <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold text-slate-500"
            data-testid="recv-confidence-none">
        AI —
      </span>
    );
  }
  const pct = Math.round((c.overall || 0) * 100);
  const cls = c.high_confidence
    ? "border-emerald-300 bg-emerald-50 text-emerald-800"
    : pct >= 60
    ? "border-amber-300 bg-amber-50 text-amber-900"
    : "border-rose-300 bg-rose-50 text-rose-800";
  return (
    <span
      data-testid={`recv-confidence-${pct}`}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${cls}`}
      title={c.high_confidence ? "High confidence" : "Manual review recommended"}
    >
      <Sparkle size={10} weight="fill" /> {pct}%
    </span>
  );
}

function StatusBadge({ status }) {
  if (status === "approved") {
    return (
      <span data-testid="recv-status-approved"
            className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">
        <CheckCircle size={12} weight="fill" /> Approved
      </span>
    );
  }
  return (
    <span data-testid="recv-status-parsed"
          className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
      <Clock size={12} weight="fill" /> Pending Review
    </span>
  );
}
